import { useState, useEffect, useCallback } from 'react';
import { logger } from 'utils/logger';

const STORAGE_KEY = 'oi_dismissed_releases';
const WHATS_NEW_URL = '/WHATS_NEW.md';

// Helper function to compare semantic versions
const compareVersions = (version1, version2) => {
    const v1Parts = version1.split('.').map(Number);
    const v2Parts = version2.split('.').map(Number);
    
    for (let i = 0; i < Math.max(v1Parts.length, v2Parts.length); i++) {
        const v1Part = v1Parts[i] || 0;
        const v2Part = v2Parts[i] || 0;
        
        if (v1Part > v2Part) return 1;
        if (v1Part < v2Part) return -1;
    }
    return 0;
};

// Parse markdown content into structured data
const parseMarkdown = (content) => {
    const lines = content.split('\n');
    const versions = [];
    let currentVersion = null;
    let currentSection = null;
    let currentItems = [];

    const sectionMappings = {
        '🚀 new features': 'features',
        'new features': 'features',
        '🐛 bug fixes': 'bugFixes',
        'bug fixes': 'bugFixes',
        '💡 improvements': 'improvements',
        'improvements': 'improvements',
        '🔧 technical changes': 'technicalChanges',
        'technical changes': 'technicalChanges',
        'technical': 'technicalChanges'
    };

    // Helper function to save current section
    const saveCurrentSection = () => {
        if (currentSection && currentItems.length > 0) {
            currentVersion[currentSection] = [...currentItems];
        }
    };

    // Helper function to save current version
    const saveCurrentVersion = () => {
        if (currentVersion) {
            saveCurrentSection();
            versions.push(currentVersion);
        }
    };

    // Helper function to parse rich content
    const parseRichContent = (text, isSubItem = false) => {
        const item = {
            text: text,
            isImportant: text.startsWith('**'),
            hasImage: text.includes('!['),
            hasLink: text.includes(']('),
            isSubItem: isSubItem,
            raw: text,
            subItems: []
        };

        // Extract title and description for bold items
        if (item.isImportant) {
            const boldMatch = text.match(/\*\*(.+?)\*\*\s*-?\s*(.+)?/);
            if (boldMatch) {
                item.title = boldMatch[1];
                item.description = boldMatch[2] || '';
            }
        }

        // Extract image info
        if (item.hasImage) {
            const imageMatch = text.match(/!\[([^\]]*)\]\(([^)]+)\)/);
            if (imageMatch) {
                item.image = {
                    alt: imageMatch[1],
                    src: imageMatch[2]
                };
            }
        }

        // Extract link info
        if (item.hasLink) {
            const linkMatch = text.match(/\[([^\]]+)\]\(([^)]+)\)/);
            if (linkMatch) {
                item.link = {
                    text: linkMatch[1],
                    href: linkMatch[2]
                };
            }
        }

        return item;
    };

    // logger.log('Starting to parse markdown...');
    
    for (let i = 0; i < lines.length; i++) {
        const line = lines[i];
        const trimmedLine = line.trim();
        
        // Skip empty lines and horizontal rules
        if (!trimmedLine || trimmedLine.startsWith('---')) {
            continue;
        }
        
        // logger.log(`Line ${i}: "${line}" (trimmed: "${trimmedLine}")`);
        
        // Version header (# Version X.X.X)
        if (trimmedLine.startsWith('# Version ')) {
            logger.log('Found version header');
            saveCurrentVersion();
            
            // Start new version
            const versionMatch = trimmedLine.match(/# Version (.+)/);
            if (versionMatch) {
                currentVersion = {
                    version: versionMatch[1],
                    releaseDate: null,
                    features: [],
                    bugFixes: [],
                    improvements: [],
                    technicalChanges: []
                };
                currentSection = null;
                currentItems = [];
            }
        }
        
        // Release date (*Released: Date*)
        else if (trimmedLine.startsWith('*Released:') && currentVersion) {
            // logger.log('Found release date');
            const dateMatch = trimmedLine.match(/\*Released:\s*(.+)\*/);
            if (dateMatch) {
                currentVersion.releaseDate = dateMatch[1];
            }
        }
        
        // Section headers (## Section Name)
        else if (trimmedLine.startsWith('## ') && currentVersion) {
            // logger.log('Found section header');
            saveCurrentSection();
            
            // Start new section
            const sectionText = trimmedLine.replace('## ', '').toLowerCase();
            currentSection = sectionMappings[sectionText] || null;
            currentItems = [];
            // logger.log(`Section: "${sectionText}" -> ${currentSection}`);
        }
        
        // List items (- Item) and sub-items (  - Subitem)
        else if (/^(\s*)- /.test(line) && currentSection) {
            // Count leading spaces before the dash
            const leadingSpaces = line.match(/^(\s*)/)[1].length;
            const isSubItem = leadingSpaces >= 2;
            
            // Remove the leading spaces and dash
            const itemText = line.replace(/^(\s*- )/, '');
            
            logger.log(`List item - Leading spaces: ${leadingSpaces}, Is sub-item: ${isSubItem}, Text: "${itemText}"`);
            
            const item = parseRichContent(itemText, isSubItem);
            
            // Handle sub-items - keep them in the same card
            if (isSubItem && currentItems.length > 0) {
                logger.log('Adding as sub-item to last main item');
                // Add to the last main item's subItems array
                const lastItem = currentItems[currentItems.length - 1];
                if (!lastItem.subItems) {
                    lastItem.subItems = [];
                }
                lastItem.subItems.push(item);
                
                // Also append to the main item's text for display in the same card
                if (!lastItem.fullText) {
                    lastItem.fullText = lastItem.text;
                }
                lastItem.fullText += `\n  - ${itemText}`;
                logger.log(`Sub-items count for last item: ${lastItem.subItems.length}`);
            } else {
                logger.log('Adding as main item');
                // Add as main item
                item.fullText = item.text;
                currentItems.push(item);
            }
        }else{
            logger.log('line', line);
            currentItems[currentItems.length-1].fullText += `\n${line}`;
        }
    }
    
    // Save last version and section
    saveCurrentVersion();
    
    logger.log('Finished parsing markdown');
    return versions;
};



const useWhatsNew = (currentAppVersion = '1.0.0') => {
    const [shouldShow, setShouldShow] = useState(false);
    const [currentVersionData, setCurrentVersionData] = useState(null);
    const [allVersions, setAllVersions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [dismissed, setDismissed] = useState(false);

    // Get dismissed versions from localStorage
    const getDismissedVersions = useCallback(() => {
        try {
            const stored = localStorage.getItem(STORAGE_KEY);
            return stored ? JSON.parse(stored) : [];
        } catch (err) {
            logger.error('Error reading dismissed versions:', err);
            return [];
        }
    }, []);

    // Save dismissed version to localStorage
    const saveDismissedVersion = useCallback((version) => {
        try {
            const dismissed = getDismissedVersions();
            if (!dismissed.includes(version)) {
                dismissed.push(version);
                localStorage.setItem(STORAGE_KEY, JSON.stringify(dismissed));
            }
        } catch (err) {
            logger.error('Error saving dismissed version:', err);
        }
    }, [getDismissedVersions]);

    // Check if version should be shown
    const shouldShowVersion = useCallback((version) => {
        const dismissed = getDismissedVersions();
        return !dismissed.includes(version) && compareVersions(version, currentAppVersion) <= 0;
    }, [currentAppVersion, getDismissedVersions]);

    // Fetch and parse markdown content
    const fetchWhatsNew = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            
            const response = await fetch(WHATS_NEW_URL);
            if (!response.ok) {
                throw new Error(`Failed to fetch changelog: ${response.status}`);
            }
            
            const content = await response.text();
            const versions = parseMarkdown(content);
            
            logger.log('Parsed versions:', versions);
            setAllVersions(versions);
            
            // Find the latest version that should be shown
            const latestVersion = versions.find(v => shouldShowVersion(v.version));
            
            if (latestVersion) {
                setCurrentVersionData(latestVersion);
                setShouldShow(true);
            } else {
                setShouldShow(false);
            }
            
        } catch (err) {
            logger.error('Error fetching What\'s New content:', err);
            setError(err.message);
            setShouldShow(false);
        } finally {
            setLoading(false);
        }
    }, [shouldShowVersion]);

    // Dismiss current version
    const dismiss = useCallback((remindLater = false) => {
        if (currentVersionData && !remindLater) {
            saveDismissedVersion(currentVersionData.version);
        }
        setShouldShow(false);
        setDismissed(true);
    }, [currentVersionData, saveDismissedVersion]);

    // Get summary stats for current version
    const getVersionSummary = useCallback(() => {
        if (!currentVersionData) return null;
        
        return {
            totalFeatures: currentVersionData.features.length,
            totalBugFixes: currentVersionData.bugFixes.length,
            totalImprovements: currentVersionData.improvements.length,
            totalTechnicalChanges: currentVersionData.technicalChanges.length,
            hasImages: currentVersionData.features.some(f => f.hasImage) ||
                      currentVersionData.bugFixes.some(f => f.hasImage) ||
                      currentVersionData.improvements.some(f => f.hasImage),
            hasLinks: currentVersionData.features.some(f => f.hasLink) ||
                     currentVersionData.bugFixes.some(f => f.hasLink) ||
                     currentVersionData.improvements.some(f => f.hasLink)
        };
    }, [currentVersionData]);

    // Get all versions for changelog view
    const getAllVersions = useCallback(() => {
        return allVersions;
    }, [allVersions]);

    // Check if there are newer versions available
    const hasNewerVersions = useCallback(() => {
        return allVersions.some(v => compareVersions(v.version, currentAppVersion) > 0);
    }, [allVersions, currentAppVersion]);

    // Initialize on mount
    useEffect(() => {
        fetchWhatsNew();
    }, [fetchWhatsNew]);

    // Refresh when app version changes
    useEffect(() => {
        if (!loading && allVersions.length > 0) {
            const latestVersion = allVersions.find(v => shouldShowVersion(v.version));
            if (latestVersion && latestVersion.version !== currentVersionData?.version) {
                setCurrentVersionData(latestVersion);
                setShouldShow(true);
                setDismissed(false);
            }
        }
    }, [currentAppVersion, allVersions, loading, shouldShowVersion, currentVersionData]);

    return {
        // State
        shouldShow: shouldShow && !dismissed,
        currentVersionData,
        loading,
        error,
        
        // Actions
        dismiss,
        refresh: fetchWhatsNew,
        
        // Utilities
        getVersionSummary,
        getAllVersions,
        hasNewerVersions,
        
        // Version comparison
        compareVersions,
        currentAppVersion
    };
};

export default useWhatsNew;